/**
 * Scroll-triggered entrance animations:
 * - Product grids: `ul.product-grid[data-product-grid-entrance]`
 * - Standalone product cards: `product-card[data-product-card-entrance]`
 * - Swatches wave: `product-card[data-product-card-swatches-entrance]` (observe card; items are `li.variant-option__swatch` in product-swatches and swatch labels inside variant-picker)
 */

const STAGGER_DEFAULT_MS = 500;
/**
 * After the card gets `product-card--entrance-visible`, wait this long before the swatch wave.
 * Card motion is `0.45s` (see product-card.liquid / product-grid.liquid). A short delay keeps
 * swatches from animating fully while the card is still at opacity ~0, but overlaps most of
 * the card motion so the entrance reads as one gesture (not card-then-swatches).
 */
const SWATCH_WAVE_AFTER_CARD_VISIBLE_MS = 90;

/** @param {Element} grid */
function getGridCards(grid) {
  return Array.from(grid.querySelectorAll(':scope > li.product-grid__item > product-card'));
}

/** @param {HTMLElement[]} cards @param {number} staggerMs */
function staggerReveal(cards, staggerMs) {
  cards.forEach((card, i) => {
    window.setTimeout(() => {
      card.classList.add('product-card--entrance-visible');
      card.classList.remove('product-card--entrance-pending');
    }, i * staggerMs);
  });
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement[]} cards
 * @param {number} staggerMs
 */
function armThenRevealRoot(root, cards, staggerMs) {
  if (!cards.length) return;
  cards.forEach((c) => {
    c.classList.remove('product-card--entrance-visible');
    c.classList.add('product-card--entrance-pending');
  });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.dataset.entranceRevealed = 'true';
      staggerReveal(cards, staggerMs);
    });
  });
}

/** @param {HTMLElement} card */
function getStandaloneBatchRoot(card) {
  if (card.closest('ul.product-grid[data-product-grid-entrance]')) return null;

  const list = card.closest('results-list, .resource-list, [data-testid="product-list"]');
  if (list instanceof HTMLElement) return list;

  const flexGroup = card.closest('.layout-panel-flex, .layout-panel-grid');
  if (flexGroup instanceof HTMLElement) {
    const n = flexGroup.querySelectorAll('product-card[data-product-card-entrance]').length;
    if (n > 1) return flexGroup;
  }

  return card;
}

/** @param {HTMLElement[]} cards */
function sortCardsDocumentOrder(cards) {
  return cards.sort((a, b) => {
    const pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/** @param {HTMLElement} root @param {HTMLElement[]} initialBatch */
function initStandaloneBatch(root, initialBatch) {
  if (root.dataset.productCardEntranceBound === 'true') return;
  root.dataset.productCardEntranceBound = 'true';

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reducedMotion) {
    root.dataset.entranceReducedMotion = 'true';
    return;
  }

  const batch = sortCardsDocumentOrder([...initialBatch]);
  const staggerMs =
    parseInt(batch[0]?.dataset.entranceStaggerMs || String(STAGGER_DEFAULT_MS), 10) || STAGGER_DEFAULT_MS;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.disconnect();
        const cards = batch.filter((c) => document.contains(c));
        armThenRevealRoot(root, cards, staggerMs);
        break;
      }
    },
    { root: null, threshold: 0.06, rootMargin: '0px 0px -8% 0px' }
  );

  io.observe(root);

  const mo = new MutationObserver((mutations) => {
    if (root.dataset.entranceRevealed !== 'true') return;

    /** @type {HTMLElement[]} */
    const newCards = [];
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof HTMLElement)) continue;
        const candidates = n.matches('product-card[data-product-card-entrance]')
          ? [n]
          : Array.from(n.querySelectorAll('product-card[data-product-card-entrance]'));
        for (const pc of candidates) {
          if (!(pc instanceof HTMLElement)) continue;
          if (pc.closest('ul.product-grid[data-product-grid-entrance]')) continue;
          if (!root.contains(pc)) continue;
          if (pc.classList.contains('product-card--entrance-visible')) continue;
          newCards.push(pc);
        }
      }
    }
    if (newCards.length) {
      armThenRevealRoot(root, sortCardsDocumentOrder(newCards), staggerMs);
    }
  });

  mo.observe(root, { childList: true, subtree: true });
}

function initStandaloneCardEntrances() {
  const all = Array.from(document.querySelectorAll('product-card[data-product-card-entrance]'));
  const standalone = all.filter((c) => !c.closest('ul.product-grid[data-product-grid-entrance]'));

  /** @type {Map<HTMLElement, HTMLElement[]>} */
  const byRoot = new Map();
  for (const card of standalone) {
    if (!(card instanceof HTMLElement)) continue;
    if (card.closest('[data-randomize-stash]')) continue;
    const r = getStandaloneBatchRoot(card);
    if (!r) continue;
    if (!byRoot.has(r)) byRoot.set(r, []);
    byRoot.get(r)?.push(card);
  }

  for (const [root, batch] of byRoot) {
    if (!(root instanceof HTMLElement) || !batch.length) continue;
    initStandaloneBatch(root, batch);
  }
}

/** @param {HTMLElement} grid */
function initGrid(grid) {
  if (grid.dataset.entranceBound === 'true') return;
  grid.dataset.entranceBound = 'true';

  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reducedMotion) {
    grid.dataset.entranceReducedMotion = 'true';
    return;
  }

  const staggerMs = parseInt(grid.dataset.entranceStaggerMs || String(STAGGER_DEFAULT_MS), 10) || STAGGER_DEFAULT_MS;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.disconnect();
        const cards = /** @type {HTMLElement[]} */ (getGridCards(grid).filter((el) => el instanceof HTMLElement));
        armThenRevealRoot(grid, cards, staggerMs);
        break;
      }
    },
    { root: null, threshold: 0.06, rootMargin: '0px 0px -8% 0px' }
  );

  io.observe(grid);

  const mo = new MutationObserver((mutations) => {
    if (grid.dataset.entranceRevealed !== 'true') return;

    /** @type {HTMLElement[]} */
    const newCards = [];
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof HTMLElement)) continue;
        if (n.matches('li.product-grid__item')) {
          const pc = n.querySelector(':scope > product-card');
          if (pc instanceof HTMLElement && !pc.classList.contains('product-card--entrance-visible')) {
            newCards.push(pc);
          }
        }
      }
    }
    if (newCards.length) {
      armThenRevealRoot(grid, newCards, staggerMs);
    }
  });

  mo.observe(grid, { childList: true });
}

/**
 * Flex `order` used on product-card swatches (left-to-right visual position).
 * @param {HTMLElement} el
 * @returns {number}
 */
function getSwatchVisualOrder(el) {
  const inline = el.style.order;
  if (inline !== '') {
    const parsed = parseInt(inline, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const fromComputed = window.getComputedStyle(el).order;
  const parsed = parseInt(fromComputed, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Wave targets: product-swatches list items and variant-picker swatch rows (no `li` wrapper).
 * Sorted by visual order (flex `order`), not DOM order.
 * @param {HTMLElement} card
 * @returns {HTMLElement[]}
 */
function getSwatchWaveItems(card) {
  const fromList = Array.from(
    card.querySelectorAll('product-swatches li.variant-option__swatch')
  ).filter((el) => el instanceof HTMLElement);

  const fromPicker = Array.from(
    card.querySelectorAll(
      'variant-picker fieldset.variant-option--swatches label.variant-option__button-label--has-swatch'
    )
  ).filter((el) => el instanceof HTMLElement);

  const seen = new Set();
  /** @type {HTMLElement[]} */
  const combined = [];
  for (const el of [...fromList, ...fromPicker]) {
    if (seen.has(el)) continue;
    seen.add(el);
    combined.push(el);
  }

  return combined.sort((a, b) => {
    const orderDiff = getSwatchVisualOrder(a) - getSwatchVisualOrder(b);
    if (orderDiff !== 0) return orderDiff;

    const pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
}

/**
 * Product cards randomize swatch layout in JS; wait so the wave follows visual left-to-right order.
 * @param {HTMLElement} card
 * @param {() => void} cb
 */
function whenSwatchLayoutReady(card, cb) {
  if (!card.querySelector('variant-picker[data-product-card-swatch-limit]')) {
    cb();
    return;
  }

  if (card.dataset.swatchesLayoutReady === 'true') {
    cb();
    return;
  }

  card.addEventListener('product-card:swatches-layout-ready', () => cb(), { once: true });
}

/**
 * Runs `cb` once the card is allowed to show the swatch wave. If the card uses the product
 * entrance, we wait until `product-card--entrance-visible` then a short offset so the wave
 * overlaps the card transition (see `SWATCH_WAVE_AFTER_CARD_VISIBLE_MS`).
 * @param {HTMLElement} card
 * @param {() => void} cb
 */
function whenCardReadyForSwatchWave(card, cb) {
  if (!card.hasAttribute('data-product-card-entrance')) {
    cb();
    return;
  }

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    cb();
  };

  const scheduleOverlappingWave = () => {
    window.setTimeout(finish, SWATCH_WAVE_AFTER_CARD_VISIBLE_MS);
  };

  if (card.classList.contains('product-card--entrance-visible')) {
    scheduleOverlappingWave();
    return;
  }

  const mo = new MutationObserver(() => {
    if (card.classList.contains('product-card--entrance-visible')) {
      mo.disconnect();
      scheduleOverlappingWave();
    }
  });
  mo.observe(card, { attributes: true, attributeFilter: ['class'] });

  window.setTimeout(() => {
    mo.disconnect();
    finish();
  }, 10000);
}

/**
 * Binds wave animation: waits for swatch list items (overflow-list may populate after first paint),
 * then observes the product-card for viewport entry (reliable box vs. a thin swatches strip).
 * @param {HTMLElement} card
 * @param {HTMLElement} inner `product-swatches` or `variant-picker` (reduced-motion marker)
 */
function scheduleSwatchesEntranceBinding(card, inner) {
  if (!(card instanceof HTMLElement) || !(inner instanceof HTMLElement)) return;
  if (card.dataset.swatchesEntranceBindingStarted === 'true') return;
  card.dataset.swatchesEntranceBindingStarted = 'true';

  const tryBind = (attempt = 0) => {
    if (card.dataset.swatchesEntranceBound === 'true') return;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reducedMotion) {
      inner.dataset.swatchesEntranceReduced = 'true';
      card.dataset.swatchesEntranceBound = 'true';
      return;
    }

    const items = getSwatchWaveItems(card);
    if (!items.length && attempt < 60) {
      requestAnimationFrame(() => tryBind(attempt + 1));
      return;
    }
    if (!items.length) {
      card.dataset.swatchesEntranceBound = 'true';
      return;
    }

    const staggerMs = parseInt(card.dataset.cardSwatchesStaggerMs || '90', 10) || 90;

    const runWave = () => {
      const waveItems = getSwatchWaveItems(card);
      if (!waveItems.length) return;
      waveItems.forEach((el) => {
        el.classList.remove('variant-option__swatch--entrance-visible');
        el.classList.add('variant-option__swatch--entrance-pending');
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          waveItems.forEach((el, i) => {
            window.setTimeout(() => {
              el.classList.remove('variant-option__swatch--entrance-pending');
              el.classList.add('variant-option__swatch--entrance-visible');
            }, i * staggerMs);
          });
        });
      });
    };

    const runWaveWhenReady = () => {
      whenSwatchLayoutReady(card, () => {
        whenCardReadyForSwatchWave(card, runWave);
      });
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.disconnect();
          runWaveWhenReady();
          break;
        }
      },
      { root: null, threshold: 0.06, rootMargin: '0px 0px -10% 0px' }
    );

    io.observe(card);
    card.dataset.swatchesEntranceBound = 'true';
  };

  tryBind();
}

function initAllSwatchesEntrances() {
  document.querySelectorAll('product-card[data-product-card-swatches-entrance]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    if (card.closest('[data-randomize-stash]')) return;
    const inner =
      card.querySelector('product-swatches') || card.querySelector('variant-picker');
    if (!(inner instanceof HTMLElement)) return;
    scheduleSwatchesEntranceBinding(card, inner);
  });
}

function scanAndInit() {
  document.querySelectorAll('ul.product-grid[data-product-grid-entrance]').forEach((grid) => {
    if (!(grid instanceof HTMLElement)) return;
    if (grid.dataset.entranceBound === 'true') return;
    initGrid(grid);
  });

  initStandaloneCardEntrances();
  initAllSwatchesEntrances();
}

function boot() {
  scanAndInit();
  document.addEventListener('shopify:section:load', () => requestAnimationFrame(scanAndInit));
  document.addEventListener('filter:update', () => requestAnimationFrame(scanAndInit));
  document.addEventListener('product-card:entrances-refresh', () => requestAnimationFrame(scanAndInit));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
